import { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Confirmación de algo que no se deshace.
 *
 * Reemplaza a `window.confirm`, que aparecía con la tipografía y los botones
 * del navegador justo en el momento de más tensión de la aplicación —cerrar un
 * acta, cancelar un plan—, y que además no distingue entre lo importante y lo
 * irreversible: todo se ve igual de grave, así que nada se ve grave.
 *
 * Se usa CON MODERACIÓN y solo para lo que no tiene vuelta atrás. Un diálogo
 * en cada acción entrena a la gente a aceptarlo sin leer, y entonces deja de
 * proteger de nada.
 *
 * Tres detalles que hacen que se sienta como una superficie y no como un
 * sobresalto: el fondo se atenúa para empujar hacia atrás lo que quedó
 * detrás, la lámina llega creciendo un punto en vez de aparecer de golpe, y el
 * foco entra en el botón seguro — que quien confirma tenga que moverse hasta
 * el otro es parte de la protección.
 */
export function Confirmar({
  abierto,
  titulo,
  detalle,
  confirmar,
  onConfirmar,
  onCancelar,
}: {
  abierto: boolean;
  titulo: string;
  /** Qué pasa exactamente si acepta. Concreto: números, nombres, consecuencias. */
  detalle: string;
  /** Texto del botón: el verbo de la acción, nunca «Aceptar». */
  confirmar: string;
  onConfirmar: () => void;
  onCancelar: () => void;
}) {
  const seguro = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!abierto) return;
    seguro.current?.focus();
    const onTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancelar();
    };
    window.addEventListener('keydown', onTecla);
    return () => window.removeEventListener('keydown', onTecla);
  }, [abierto, onCancelar]);

  if (!abierto) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmar-titulo"
      onClick={onCancelar}
      className="fixed inset-0 z-50 flex items-end justify-center bg-navy-900/70 p-4 backdrop-blur-sm motion-safe:animate-[velo_var(--transition-base)_var(--ease-salida)] sm:items-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-border bg-surface-raised p-5 shadow-lg motion-safe:animate-[lamina_var(--transition-lenta)_var(--ease-salida)]"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-error" aria-hidden="true" />
          <div className="min-w-0">
            <h2 id="confirmar-titulo" className="font-display text-base font-semibold text-content">
              {titulo}
            </h2>
            <p className="mt-1.5 font-sans text-sm leading-relaxed text-content-muted">{detalle}</p>
          </div>
        </div>
        {/* El destructivo va a la derecha y separado: el pulgar que viene
            subiendo encuentra primero la salida, no la consecuencia. */}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            ref={seguro}
            type="button"
            onClick={onCancelar}
            className="inline-flex min-h-touch items-center justify-center rounded-lg border border-border px-4 font-sans text-sm font-semibold text-content"
          >
            Mejor no
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            className="inline-flex min-h-touch items-center justify-center rounded-lg bg-error px-4 font-sans text-sm font-semibold text-content-inverse"
          >
            {confirmar}
          </button>
        </div>
      </div>
    </div>
  );
}
